import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp, NumInput } from '../../components/ui/Inputs.jsx';
import { dateSuisse } from '../../lib/dates';
import { C } from '../../theme/palette';
import { tr } from '../../lib/i18n';

/** Les trois canaux, dans les mots de l'écran. */
const CANAUX = [
  ['EMAIL', '📧 E-mail'],
  ['SMS', '💬 SMS'],
  ['LES_DEUX', '📧 + 💬 Les deux'],
];

/** Les canaux de la relance des offres : WhatsApp en plus, envoyé d'un toucher. */
const CANAUX_RELANCE = [...CANAUX, ['WHATSAPP', '🟢 WhatsApp']];

/** Interrupteur, dans le style des réglages. */
function Interrupteur({ label, sub, on, onChange, testId }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '9px 0',
        borderBottom: '1px solid ' + C.border,
      }}
    >
      <div style={{ flex: 1, marginRight: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700 }}>{label}</div>
        {sub && <div style={{ fontSize: 8.5, color: C.muted, marginTop: 2 }}>{sub}</div>}
      </div>
      <div
        role="switch"
        aria-checked={on}
        aria-label={label}
        data-testid={testId}
        onClick={() => onChange(!on)}
        style={{
          width: 42,
          height: 24,
          borderRadius: 99,
          background: on ? C.green : C.border,
          position: 'relative',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background .15s',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 20 : 2,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left .15s',
          }}
        />
      </div>
    </div>
  );
}

/** Choix du canal, en pastilles. */
function Canal({ valeur, onChange, smsConfigure, nom, canaux = CANAUX }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {canaux.map(([v, lb]) => {
          const on = valeur === v;
          return (
            <div
              key={v}
              role="radio"
              aria-checked={on}
              aria-label={tr(nom) + ' ' + tr(lb)}
              onClick={() => onChange(v)}
              style={{
                padding: '7px 11px',
                borderRadius: 9,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1.5px solid ' + (on ? C.teal : C.border),
                background: on ? C.teal : '#fff',
                color: on ? '#fff' : C.ink,
              }}
            >
              {lb}
            </div>
          );
        })}
      </div>
      {!smsConfigure && (valeur === 'SMS' || valeur === 'LES_DEUX') && (
        <div style={{ fontSize: 10, color: C.amber, fontWeight: 700, marginTop: 6 }}>
          ⚠ Aucun prestataire SMS n&apos;est raccordé au serveur (variable SMS_URL) : les SMS
          iraient au journal du serveur, pas au client.
        </div>
      )}
    </div>
  );
}

/** Un montant tapé « 20 », « 20.50 » ou « 20,50 » ; vide = 0 (sans frais), absent = la valeur par défaut. */
const montant = (v, defaut) => {
  if (v === undefined || v === null) return defaut;
  if (v === '') return 0;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : defaut;
};

const nombre = (v, defaut) => {
  const n = parseInt(String(v ?? '').replace(',', '.'), 10);
  return Number.isFinite(n) ? n : defaut;
};

/**
 * Réglages des envois automatiques : rappels de facture, rappel de rendez-vous,
 * alerte des devis qui expirent.
 *
 * <p>Ces réglages vivent sur le serveur, et pour cause : c'est lui qui envoie,
 * chaque jour à l'heure dite, que l'application soit ouverte ou non. La carte
 * montre le texte qui partira, pour que le patron le lise avant d'activer.
 *
 * @returns {JSX.Element} la carte
 */
export function EnvoisAutoCard() {
  const { session } = useSession();
  const peutModifier = session?.role === 'patron';
  const [reglages, setReglages] = useState(null);
  const [apercus, setApercus] = useState([]);
  const [apercuOuvert, setApercuOuvert] = useState(null);
  const [erreur, setErreur] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    let annule = false;
    envoisApi
      .reglages()
      .then((r) => {
        if (!annule) setReglages(r);
      })
      .catch(() => {
        if (!annule) setErreur('Les réglages d’envoi n’ont pas pu être lus.');
      });
    envoisApi
      .apercu()
      .then((liste) => {
        if (!annule) setApercus(liste || []);
      })
      .catch(() => {
        // L'apercu est un confort : sans lui, les reglages restent utilisables.
      });
    return () => {
      annule = true;
    };
  }, []);

  const set = (patch) => {
    setConfirmation('');
    setReglages((p) => ({ ...p, ...patch }));
  };

  const enregistre = async () => {
    setEnCours(true);
    setErreur('');
    setConfirmation('');
    try {
      const sauve = await envoisApi.enregistreReglages({
        rappelsActifs: !!reglages.rappelsActifs,
        rappelJours1: nombre(reglages.rappelJours1, 10),
        rappelJours2: nombre(reglages.rappelJours2, 20),
        rappelJours3: nombre(reglages.rappelJours3, 30),
        rappelFrais1: montant(reglages.rappelFrais1, 0),
        rappelFrais2: montant(reglages.rappelFrais2, 20),
        rappelFrais3: montant(reglages.rappelFrais3, 40),
        rappelCanal: reglages.rappelCanal || 'EMAIL',
        heureEnvoi: reglages.heureEnvoi || '08:00',
        rdvRappelActif: !!reglages.rdvRappelActif,
        rdvCanal: reglages.rdvCanal || 'EMAIL',
        devisAlerteActive: !!reglages.devisAlerteActive,
        devisAlerteJours: nombre(reglages.devisAlerteJours, 3),
        relanceDevisActive: !!reglages.relanceDevisActive,
        relanceDevisJours: Math.min(365, Math.max(1, nombre(reglages.relanceDevisJours, 15))),
        relanceDevisCanal: reglages.relanceDevisCanal || 'EMAIL',
      });
      setReglages(sauve);
      // L'aperçu montre le texte de relance qui vient d'être écrit.
      envoisApi
        .apercu()
        .then((liste) => setApercus(liste || []))
        .catch(() => {});
      setConfirmation('Réglages enregistrés. Le serveur passera à ' + sauve.heureEnvoi + '.');
    } catch (e) {
      setErreur(e?.message || 'L’enregistrement a échoué.');
    } finally {
      setEnCours(false);
    }
  };

  const titre = { fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase' };

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="📨"
        title="Envois automatiques"
        color={C.teal}
        sub="Le serveur envoie seul, chaque jour à l’heure réglée — e-mail et SMS"
      />
      <div style={{ padding: 13 }}>
        {!reglages && !erreur && (
          <div style={{ fontSize: 8.5, color: C.muted }}>Lecture des réglages…</div>
        )}
        {reglages && (
          <>
            <div style={{ ...titre, marginBottom: 4 }}>🔔 Rappels de facture</div>
            <Interrupteur
              label="Rappeler automatiquement les factures échues"
              sub="Trois niveaux, jamais deux fois le même ; rien pour une facture payée, annulée ou en brouillon"
              on={!!reglages.rappelsActifs}
              onChange={(v) => set({ rappelsActifs: v })}
              testId="rappels-actifs"
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {[
                ['rappelJours1', '1er rappel'],
                ['rappelJours2', '2e rappel'],
                ['rappelJours3', '3e rappel'],
              ].map(([cle, lb]) => (
                <div key={cle} style={{ flex: 1 }}>
                  <Inp label={lb}>
                    <NumInput
                      value={reglages[cle]}
                      onChange={(e) => set({ [cle]: e.target.value })}
                      placeholder="jours"
                    />
                  </Inp>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: -4 }}>
              Jours après l’échéance. Chaque niveau vient après le précédent.
            </div>
            {/* Les frais de chaque rappel : le rappel automatique les ajoute au
                solde, et le rappel fait à la main les propose. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {[
                ['rappelFrais1', 'Frais du 1er rappel', 0],
                ['rappelFrais2', 'Frais du 2e rappel', 20],
                ['rappelFrais3', 'Frais du 3e rappel', 40],
              ].map(([cle, lb, defaut]) => (
                <div key={cle} style={{ flex: 1 }}>
                  <Inp label={lb}>
                    <NumInput
                      value={reglages[cle] ?? defaut}
                      onChange={(e) => set({ [cle]: e.target.value })}
                      placeholder="0.00"
                    />
                  </Inp>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: -4 }}>
              Ajoutés au solde de la facture sur le rappel (e-mail, PDF et QR). 0 = sans frais.
            </div>
            <Canal
              nom="Canal des rappels"
              valeur={reglages.rappelCanal}
              onChange={(v) => set({ rappelCanal: v })}
              smsConfigure={!!reglages.smsConfigure}
            />

            <div style={{ ...titre, marginTop: 16, marginBottom: 4 }}>🕗 Heure d’envoi</div>
            <Inp
              label="Heure du passage quotidien (heure suisse)"
              type="time"
              value={reglages.heureEnvoi || '08:00'}
              onChange={(e) => set({ heureEnvoi: e.target.value })}
            />
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: -4 }}>
              {reglages.dernierTraitement
                ? 'Dernier passage : ' + dateSuisse(reglages.dernierTraitement)
                : 'Le serveur n’est encore jamais passé pour cette société.'}
            </div>

            <div style={{ ...titre, marginTop: 16, marginBottom: 4 }}>📅 Rendez-vous</div>
            <Interrupteur
              label="Rappeler le rendez-vous au client la veille"
              sub="Pour les rendez-vous qui nomment un client"
              on={!!reglages.rdvRappelActif}
              onChange={(v) => set({ rdvRappelActif: v })}
              testId="rdv-actif"
            />
            <Canal
              nom="Canal des rendez-vous"
              valeur={reglages.rdvCanal}
              onChange={(v) => set({ rdvCanal: v })}
              smsConfigure={!!reglages.smsConfigure}
            />

            <div style={{ ...titre, marginTop: 16, marginBottom: 4 }}>📝 Devis</div>
            <Interrupteur
              label="Prévenir le patron des devis qui expirent"
              sub="Un e-mail à l’adresse de la société, sinon au compte de direction"
              on={!!reglages.devisAlerteActive}
              onChange={(v) => set({ devisAlerteActive: v })}
              testId="devis-actif"
            />
            <div style={{ marginTop: 10, maxWidth: 200 }}>
              <Inp label="Jours avant l’expiration">
                <NumInput
                  value={reglages.devisAlerteJours}
                  onChange={(e) => set({ devisAlerteJours: e.target.value })}
                  placeholder="3"
                />
              </Inp>
            </div>

            <div style={{ ...titre, marginTop: 16, marginBottom: 4 }}>⏰ Offres sans réponse</div>
            <Interrupteur
              label="Relancer automatiquement les offres sans réponse"
              sub="Une seule relance par offre, ni acceptée ni refusée ni expirée. C’est une option : rien ne part tant qu’elle est éteinte."
              on={!!reglages.relanceDevisActive}
              onChange={(v) => set({ relanceDevisActive: v })}
              testId="relance-devis-active"
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {[
                [15, '15 jours'],
                [30, '1 mois'],
              ].map(([j, lb]) => {
                const on = nombre(reglages.relanceDevisJours, 15) === j;
                return (
                  <div
                    key={j}
                    role="radio"
                    aria-checked={on}
                    onClick={() => set({ relanceDevisJours: j })}
                    style={{
                      padding: '7px 11px',
                      borderRadius: 9,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: '1.5px solid ' + (on ? C.teal : C.border),
                      background: on ? C.teal : '#fff',
                      color: on ? '#fff' : C.ink,
                    }}
                  >
                    {lb}
                  </div>
                );
              })}
              <div style={{ width: 110 }}>
                <NumInput
                  aria-label="Jours avant la relance"
                  value={reglages.relanceDevisJours ?? 15}
                  onChange={(e) => set({ relanceDevisJours: e.target.value })}
                  placeholder="jours"
                />
              </div>
            </div>
            <div style={{ fontSize: 8.5, color: C.muted, marginTop: 4 }}>
              Jours après l’envoi de l’offre au client.
            </div>
            <Canal
              nom="Canal de la relance"
              valeur={reglages.relanceDevisCanal || 'EMAIL'}
              onChange={(v) => set({ relanceDevisCanal: v })}
              smsConfigure={!!reglages.smsConfigure}
              canaux={CANAUX_RELANCE}
            />
            {reglages.relanceDevisCanal === 'WHATSAPP' && (
              <div style={{ fontSize: 9.5, color: C.ink, marginTop: 6, lineHeight: 1.5 }}>
                WhatsApp ne permet pas à SwissTech d’écrire à votre place : le jour venu, l’offre
                apparaît dans « À relancer » (Offres) avec le message prêt, et un toucher l’envoie
                depuis votre téléphone.
              </div>
            )}
            <div style={{ fontSize: 9.5, color: C.ink, marginTop: 8, lineHeight: 1.5 }}>
              ✍️{' '}
              {tr(
                'Le texte de la relance se change plus bas, dans « Textes des rappels et relances ».',
              )}
            </div>

            {apercus.length > 0 && (
              <>
                <div style={{ ...titre, marginTop: 12, marginBottom: 6 }}>
                  👁 Aperçu du texte envoyé
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {apercus.map((a) => (
                    <Btn
                      key={a.type}
                      label={a.titre}
                      sm
                      ghost={apercuOuvert !== a.type}
                      color={C.navy}
                      onClick={() => setApercuOuvert(apercuOuvert === a.type ? null : a.type)}
                    />
                  ))}
                </div>
                {apercus
                  .filter((a) => a.type === apercuOuvert)
                  .map((a) => (
                    <div
                      key={a.type}
                      data-testid="apercu-texte"
                      style={{
                        marginTop: 8,
                        background: C.navyXL,
                        border: '1px solid ' + C.border,
                        borderRadius: 9,
                        padding: '9px 11px',
                        fontSize: 10,
                        lineHeight: 1.5,
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>Objet : {a.objet}</div>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 5 }}>{a.corps}</div>
                      {a.sms && (
                        <div style={{ marginTop: 7, color: C.grey }}>
                          <b>SMS :</b> {a.sms}
                        </div>
                      )}
                    </div>
                  ))}
              </>
            )}

            <div style={{ marginTop: 14 }}>
              {peutModifier ? (
                <Btn
                  label={enCours ? '⏳ Enregistrement…' : '✓ Enregistrer les envois automatiques'}
                  color={C.teal}
                  full
                  disabled={enCours}
                  onClick={enregistre}
                />
              ) : (
                <div style={{ fontSize: 8.5, color: C.muted }}>
                  Seul le patron modifie ces réglages.
                </div>
              )}
            </div>
          </>
        )}
        {confirmation && (
          <div style={{ marginTop: 8, fontSize: 10, color: C.green, fontWeight: 700 }}>
            ✓ {confirmation}
          </div>
        )}
        {erreur && (
          <div style={{ marginTop: 8, fontSize: 10, color: C.red, fontWeight: 700 }}>{erreur}</div>
        )}
      </div>
    </Card>
  );
}
